import type { Locale } from "@i18n/react";

import { HEADERS } from "../../constants/headers/headers.constants";
import { HEADERS_AUTHENTICATED } from "../../constants/headers/headersAuthenticated.constants";
import { HEADERS_OTHER } from "../../constants/headers/headersOther.constants";
import { HEADERS_SEARCH } from "../../constants/headers/headersSearch.constants";
import { HEADERS_SEARCH_SERVICE } from "../../constants/headers/headersSearchService.constants";
import { HEADERS_TRUSTED } from "../../constants/headers/headersTrusted.constants";
import { getCookie } from "../../lib/cookies/utils/getCookie";
import { SESSION_STORAGE_KEY_GOOGLE_CLICK_IDS } from "../../lib/sessionStorage/sessionStorageGoogleClickIds.sessionStorage";
import { SESSION_STORAGE_KEY_PINTEREST_CLICK_ID } from "../../lib/sessionStorage/sessionStoragePinterestClickId.sessionStorage";
import { getSessionRequestHeaders } from "../../session/utils/getSessionRequestHeaders";
import { determineClientOrServer } from "../../utils/determineClientOrServer";
import { getI18nRequestHeaders } from "../../utils/getI18nRequestHeaders";
import type { RequestType } from "../types/enums/requestType.enum";

export type GetRequestHeadersProps = {
  requestType: RequestType;
  values?: {
    appSession?: string;
    locale?: Locale;
    token?: string;
  };
};

export const getRequestHeaders = async ({
  values,
  requestType,
}: GetRequestHeadersProps): Promise<HeadersInit> => {
  let headers: HeadersInit = HEADERS;

  if (requestType === "other") return HEADERS_OTHER;

  if (requestType === "searchService")
    return {
      ...HEADERS_SEARCH_SERVICE,
      ...(values?.token ? { Authorization: `Bearer ${values.token}` } : {}),
    };

  if (values?.appSession)
    headers = {
      ...headers,
      "X-Hallow-App-Session": values?.appSession,
    };

  headers = {
    ...headers,
    ...(await getI18nRequestHeaders({ valueLocale: values?.locale })),
  };

  if (requestType === "authenticated")
    headers = {
      ...headers,
      ...(await getSessionRequestHeaders({ valueToken: values?.token })),
    };

  if (determineClientOrServer() === "client") {
    const urlParams = new URLSearchParams(window.location.search);
    const storedClickIds = JSON.parse(
      sessionStorage.getItem(SESSION_STORAGE_KEY_GOOGLE_CLICK_IDS) ?? "{}",
    ) as Partial<Record<"gclid" | "wbraid" | "gbraid", string>>;

    if (urlParams.get("isAndroid") === "true")
      headers = { ...headers, "X-Cross-Platform-Origin": "Android" };
    else if (urlParams.get("isIos") === "true")
      headers = { ...headers, "X-Cross-Platform-Origin": "iOS" };

    headers = {
      ...headers,
      "X-CID": getCookie({ key: "_fbc" }),
      "X-PID": getCookie({ key: "_fbp" }),
      "X-Gclid":
        urlParams.get("gclid") ??
        storedClickIds.gclid ??
        getCookie({ key: "gclid" }),
      "X-Wbraid":
        urlParams.get("wbraid") ??
        storedClickIds.wbraid ??
        getCookie({ key: "wbraid" }),
      "X-Gbraid":
        urlParams.get("gbraid") ??
        storedClickIds.gbraid ??
        getCookie({ key: "gbraid" }),
      "X-Epik":
        urlParams.get("epik") ??
        sessionStorage.getItem(SESSION_STORAGE_KEY_PINTEREST_CLICK_ID) ??
        getCookie({ key: "_epik" }),
      "X-Timezone": Intl.DateTimeFormat().resolvedOptions().timeZone,
    };
  }

  switch (requestType) {
    case "authenticated":
      return { ...headers, ...HEADERS_AUTHENTICATED };
    case "search":
      return { ...headers, ...HEADERS_SEARCH };
    case "trusted":
      return { ...headers, ...HEADERS_TRUSTED };
    case "default":
    default:
      return headers;
  }
};
